import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

/**
 * Custom error handler that catches chunk loading errors and gracefully reloads the app.
 * This handles the common SPA scenario where:
 * 1. User loads the app
 * 2. A deployment happens (chunk hashes change)
 * 3. Browser still has old index.html with referencing old chunk hashes
 * 4. Angular tries to lazy-load a chunk that no longer exists
 * 5. Webpack throws ChunkLoadError
 *
 * Instead of staying in a broken state, we show a message to the user and reload.
 */
@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
  private static readonly RELOAD_GUARD_KEY = 'fbw_chunk_reload_guard';
  private static readonly MAX_RELOAD_ATTEMPTS = 1;
  private static readonly RELOAD_GUARD_WINDOW_MS = 60_000;
  private static readonly CHUNK_LOAD_ERROR_PATTERNS = [
    /Loading chunk \d+ failed/i,
    /loaded for chunk/i,
    /Unable to load/i,
    /Failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /Importing a module script failed/i,
  ];

  private isReloadScheduled = false;
  private reloadTimeoutId: number | null = null;
  private sentryErrorHandler: ErrorHandler;

  constructor() {
    // Create a Sentry error handler for delegation
    this.sentryErrorHandler = Sentry.createErrorHandler({
      showDialog: false,
    });
  }

  handleError(error: Error): void {
    // Check if this is a chunk loading error
    if (this.isChunkLoadError(error)) {
      this.handleChunkLoadError(error);
      return;
    }
    // For other errors, delegate to Sentry error handler
    this.sentryErrorHandler.handleError(error);
  }

  /**
   * Detects if the error is a chunk loading error
   * Common patterns:
   * - "ChunkLoadError" / "Loading chunk X failed" (webpack)
   * - "Failed to fetch dynamically imported module" (esbuild, Chrome/Edge)
   * - "error loading dynamically imported module" (esbuild, Firefox)
   * - "Importing a module script failed" (esbuild, Safari)
   */
  private isChunkLoadError(error: Error): boolean {
    if (!error) {
      return false;
    }

    const errorMsg = error.message || '';
    const errorName = error.name || '';

    return (
      errorName === 'ChunkLoadError' ||
      ChunkLoadErrorHandler.CHUNK_LOAD_ERROR_PATTERNS.some((pattern) =>
        pattern.test(errorMsg)
      )
    );
  }

  /**
   * Handles chunk loading errors by showing a message and reloading the app
   */
  private handleChunkLoadError(error: Error): void {
    if (this.isReloadScheduled) {
      return;
    }

    if (this.isOffline()) {
      // eslint-disable-next-line no-console
      console.warn('Chunk load error while offline. Skipping reload.', error);

      this.showRecoveryMessage(
        'You appear to be offline, so part of the app could not be loaded.',
        'Connection lost'
      );
      return;
    }

    if (!this.canAttemptReload()) {
      // eslint-disable-next-line no-console
      console.error(
        'Chunk load error persists after reload attempt. Showing recovery actions.',
        error
      );

      this.showRecoveryMessage();
      // Report the persistent chunk error to Sentry
      this.sentryErrorHandler.handleError(error);
      return;
    }

    this.isReloadScheduled = true;
    this.recordReloadAttempt();

    // Log the error for debugging
    // eslint-disable-next-line no-console
    console.warn(
      'Chunk load error detected. A new version of the app is available. Reloading...',
      error
    );

    // Report the chunk error to Sentry for monitoring
    Sentry.captureMessage(
      'Chunk load error detected. App will reload.',
      'warning'
    );

    // Show a banner notification or alert to the user
    this.showReloadMessage();

    // Give the user a moment to see the message, then reload
    if (this.reloadTimeoutId !== null) {
      clearTimeout(this.reloadTimeoutId);
    }

    this.reloadTimeoutId = window.setTimeout(() => {
      window.location.reload();
    }, 2000);
  }

  private isOffline(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  }

  private canAttemptReload(): boolean {
    try {
      const state = this.readReloadGuardState();
      const now = Date.now();

      if (
        now - state.firstAttemptAt >
        ChunkLoadErrorHandler.RELOAD_GUARD_WINDOW_MS
      ) {
        this.clearReloadGuardState();
        return true;
      }

      return state.attempts < ChunkLoadErrorHandler.MAX_RELOAD_ATTEMPTS;
    } catch {
      return true;
    }
  }

  private recordReloadAttempt(): void {
    try {
      const now = Date.now();
      const state = this.readReloadGuardState();

      if (
        now - state.firstAttemptAt >
        ChunkLoadErrorHandler.RELOAD_GUARD_WINDOW_MS
      ) {
        this.writeReloadGuardState({ attempts: 1, firstAttemptAt: now });
        return;
      }

      this.writeReloadGuardState({
        attempts: state.attempts + 1,
        firstAttemptAt: state.firstAttemptAt,
      });
    } catch {
      // Ignore storage failures and continue with in-memory guard only.
    }
  }

  private readReloadGuardState(): { attempts: number; firstAttemptAt: number } {
    const raw = sessionStorage.getItem(ChunkLoadErrorHandler.RELOAD_GUARD_KEY);

    if (!raw) {
      return { attempts: 0, firstAttemptAt: Date.now() };
    }

    const parsed = JSON.parse(raw) as {
      attempts?: number;
      firstAttemptAt?: number;
    };

    return {
      attempts: Number.isFinite(parsed.attempts) ? Number(parsed.attempts) : 0,
      firstAttemptAt: Number.isFinite(parsed.firstAttemptAt)
        ? Number(parsed.firstAttemptAt)
        : Date.now(),
    };
  }

  private writeReloadGuardState(state: {
    attempts: number;
    firstAttemptAt: number;
  }): void {
    sessionStorage.setItem(
      ChunkLoadErrorHandler.RELOAD_GUARD_KEY,
      JSON.stringify(state)
    );
  }

  private clearReloadGuardState(): void {
    sessionStorage.removeItem(ChunkLoadErrorHandler.RELOAD_GUARD_KEY);
  }

  /**
   * Shows a message to the user that the app is being reloaded
   * This creates a simple overlay that doesn't depend on Angular
   */
  private showReloadMessage(): void {
    // Only show if no message already exists
    if (document.getElementById('chunk-load-error-message')) {
      return;
    }

    const messageDiv = document.createElement('div');
    messageDiv.id = 'chunk-load-error-message';
    messageDiv.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.95);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <div style="
          text-align: center;
          padding: 2rem;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          max-width: 400px;
        ">
          <h2 style="margin: 0 0 1rem 0; color: #1976d2;">
            New Version Available
          </h2>
          <p style="
            margin: 0 0 1rem 0;
            color: #666;
            line-height: 1.5;
          ">
            A new version of the app has been deployed. Reloading now...
          </p>
          <div style="
            display: flex;
            justify-content: center;
            gap: 0.5rem;
            margin-top: 1rem;
          ">
            <div style="
              display: inline-block;
              width: 8px;
              height: 8px;
              background: #1976d2;
              border-radius: 50%;
              animation: pulse 1.4s infinite;
            "></div>
            <div style="
              display: inline-block;
              width: 8px;
              height: 8px;
              background: #1976d2;
              border-radius: 50%;
              animation: pulse 1.4s infinite 0.2s;
            "></div>
            <div style="
              display: inline-block;
              width: 8px;
              height: 8px;
              background: #1976d2;
              border-radius: 50%;
              animation: pulse 1.4s infinite 0.4s;
            "></div>
          </div>
          <style>
            @keyframes pulse {
              0%, 100% { opacity: 0.3; }
              50% { opacity: 1; }
            }
          </style>
        </div>
      </div>
    `;

    document.body.appendChild(messageDiv);
  }

  private showRecoveryMessage(
    bodyText = 'The app could not load updated files after an automatic retry.',
    title = 'Unable to load latest version'
  ): void {
    if (document.getElementById('chunk-load-error-message')) {
      return;
    }

    const messageDiv = document.createElement('div');
    messageDiv.id = 'chunk-load-error-message';
    messageDiv.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.95);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <div style="
          text-align: center;
          padding: 2rem;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          max-width: 460px;
        ">
          <h2 style="margin: 0 0 1rem 0; color: #1976d2;">
            ${title}
          </h2>
          <p style="margin: 0 0 1rem 0; color: #666; line-height: 1.5;">
            ${bodyText}
          </p>
          <p style="margin: 0 0 1.5rem 0; color: #666; line-height: 1.5;">
            Please reload now. If this keeps happening, clear browser cache and try again.
          </p>
          <div style="
            display: flex;
            justify-content: center;
            gap: 0.75rem;
          ">
            <button id="chunk-load-error-dismiss-button" type="button" style="
              background: #fff;
              color: #1976d2;
              border: 1px solid #1976d2;
              border-radius: 4px;
              padding: 0.6rem 1rem;
              cursor: pointer;
              font-size: 14px;
            ">
              Continue without reload
            </button>
            <button id="chunk-load-error-reload-button" type="button" style="
              background: #1976d2;
              color: #fff;
              border: none;
              border-radius: 4px;
              padding: 0.6rem 1rem;
              cursor: pointer;
              font-size: 14px;
            ">
              Reload now
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(messageDiv);

    const reloadButton = document.getElementById(
      'chunk-load-error-reload-button'
    );
    const dismissButton = document.getElementById(
      'chunk-load-error-dismiss-button'
    );

    dismissButton?.addEventListener('click', () => {
      messageDiv.remove();
    });
    reloadButton?.addEventListener('click', () => window.location.reload());
  }
}
